/**
 * Login/reset backdrop from the creator's `brand_settings` blob.
 *
 * The relevant legacy FlexiFunnels keys, as written by the membership admin
 * (SettingsController::saveBrand) and the older newflexifunnels admin:
 *
 *   bgmode          "image" | "color"
 *   bgtype          image fit: cover | contain | repeat | no-repeat | repeat-x | repeat-y
 *   bgcolortype     "color" | "gradient"   (only meaningful when bgmode === "color")
 *   bgcolor         "#rrggbb"
 *   bggradientColor one full CSS gradient string, e.g. "linear-gradient(90deg, … )"
 *
 * `brand_settings` reaches us verbatim — flexiapi's getmembership/getfunnel does
 * no key whitelisting — so every one of these is already on the wire.
 */

const GRADIENT_RE = /^(?:repeating-)?(?:linear|radial)-gradient\s*\(.+\)$/is;

/** The gradient string when the creator has one active, else "". */
export function activeGradient(brand) {
  const b = brand || {};
  if (b.bgmode !== "color" || b.bgcolortype !== "gradient") return "";
  // Vendor prefixes: legacy grapick rows can still carry them, and the admin only
  // strips them on save, so old rows reach us unprefixed-only after a re-save.
  const g = String(b.bggradientColor || "")
    .replace(/-(?:webkit|moz|o|ms)-/gi, "")
    .trim();
  return GRADIENT_RE.test(g) ? g : "";
}

/**
 * The single source of truth for the brand backdrop: image, solid colour, or
 * gradient. Returns a React style object.
 */
export function brandBackgroundStyle(brand) {
  const b = brand || {};

  if (b.bgmode === "color") {
    const gradient = activeGradient(b);
    // The `background` shorthand, NOT `backgroundColor` — the longhand is invalid
    // for a gradient, so browsers drop it and the page renders transparent.
    return gradient ? { background: gradient } : { backgroundColor: b.bgcolor || "#012329" };
  }

  // `bgtype` doubles as background-size (cover/contain) or background-repeat
  // (repeat/no-repeat/repeat-x/repeat-y), depending on what the creator picked.
  const fit = b.bgtype || "cover";
  const sized = fit === "cover" || fit === "contain";
  return {
    backgroundImage: `url("${b.bgimg}")`,
    backgroundPosition: "center",
    backgroundSize: sized ? fit : "auto",
    backgroundRepeat: sized ? "no-repeat" : fit,
  };
}
